import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { errorMessage } from "@atlas/shared";
import { Search, ShieldAlert, Trophy, UsersRound } from "lucide-react";
import {
  adminDeleteGroup,
  adminGrantChallengePrize,
  adminRejectChallengePrize,
  adminSetChallengePrize,
  fetchAdminChallenges,
  fetchAdminGroups,
  fetchAdminPrizeItems,
  type AdminChallengeRow,
  type AdminGroupRow,
  type AdminPrizeInput,
} from "../../api/groups";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { fmtRange } from "../groups/group-ui";

// Admin: groups (spec 32 / ATL-151). Challenge prize queue first (it is the
// part that needs action), then a searchable group list with delete.
// Prizes: attach Waypoints or a store item (approves the request), then grant
// only after checking the winners' activities. Grant-only cosmetics (the beta
// surveyor set and anything else seeded inactive or unpriced) are never
// offered in the picker.

const CARD: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-lg)",
  boxShadow: "var(--shadow-sm), var(--ring-top)",
  padding: 16,
  minWidth: 0,
};

const INPUT: React.CSSProperties = {
  height: "var(--control-sm)",
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  outline: "none",
  minWidth: 0,
};

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "–";
}

// ─── Prize queue ──────────────────────────────────────────────────────────────

function PrizeEditor({ row, onDone }: { row: AdminChallengeRow; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = React.useState<AdminPrizeInput["kind"]>(row.prizeKind ?? "waypoints");
  const [amount, setAmount] = React.useState(row.prizeAmount != null ? String(row.prizeAmount) : "");
  const [itemId, setItemId] = React.useState(row.prizeItemId ?? "");

  // Server-filtered: active, priced cosmetics only. Grant-only sets never appear.
  const itemsQuery = useQuery({
    queryKey: ["admin/challenges/prize-items"],
    queryFn: fetchAdminPrizeItems,
    enabled: kind === "cosmetic",
  });
  const items = itemsQuery.data?.items ?? [];

  const mutation = useMutation({
    mutationFn: () =>
      adminSetChallengePrize(
        row.challenge.id,
        kind === "waypoints" ? { kind, amount: Number(amount), itemId: null } : { kind, itemId, amount: null },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin/challenges"] });
      onDone();
    },
  });

  const amountNum = Number(amount);
  const valid = kind === "waypoints" ? Number.isInteger(amountNum) && amountNum > 0 : !!itemId;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, borderRadius: "var(--radius-md)", background: "var(--surface-sunken)" }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={kind} onChange={(e) => setKind(e.target.value as AdminPrizeInput["kind"])} style={INPUT}>
          <option value="waypoints">Waypoints</option>
          <option value="cosmetic">Store item</option>
        </select>
        {kind === "waypoints" ? (
          <input
            type="number"
            min={1}
            step={1}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount per winner"
            style={{ ...INPUT, width: 170 }}
          />
        ) : (
          <select value={itemId} onChange={(e) => setItemId(e.target.value)} style={{ ...INPUT, flex: "1 1 220px" }} disabled={itemsQuery.isLoading}>
            <option value="">{itemsQuery.isLoading ? "Loading items…" : "Choose an item"}</option>
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.kind}, {i.rarity})
              </option>
            ))}
          </select>
        )}
        <Button size="sm" variant="secondary" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving…" : row.challenge.prizeStatus === "approved" ? "Update prize" : "Approve with this prize"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
      <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
        Each tied winner gets the prize. Only active, priced store items are listed.
      </span>
      {mutation.isError && (
        <span style={{ fontSize: 12, color: "var(--status-danger)" }}>{errorMessage(mutation.error, "Couldn't save the prize.")}</span>
      )}
    </div>
  );
}

function PrizeRow({ row }: { row: AdminChallengeRow }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = React.useState(false);
  const c = row.challenge;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin/challenges"] });

  const grantMutation = useMutation({ mutationFn: () => adminGrantChallengePrize(c.id), onSuccess: invalidate });
  const rejectMutation = useMutation({ mutationFn: () => adminRejectChallengePrize(c.id), onSuccess: invalidate });

  const finalised = c.state === "finalised";
  const prizeText =
    row.prizeKind === "waypoints"
      ? `${(row.prizeAmount ?? 0).toLocaleString("en-GB")} Waypoints each`
      : row.prizeKind === "cosmetic"
        ? (c.prize?.itemName ?? `Store item ${row.prizeItemId ?? ""}`)
        : null;

  function handleGrant() {
    if (window.confirm("Grant the prize to every winner listed? Check their activities first. This can't be undone from here.")) {
      grantMutation.mutate();
    }
  }

  function handleReject() {
    if (window.confirm("Reject the prize for this challenge? The challenge itself carries on.")) rejectMutation.mutate();
  }

  return (
    <div style={{ ...CARD, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0, flex: "1 1 260px" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", overflowWrap: "anywhere" }}>{c.title}</div>
          <div className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {row.groupName} · {row.memberCount.toLocaleString("en-GB")} members · {c.target.name} · {fmtRange(c.startsOn, c.endsOn)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Badge variant="neutral">{c.state}</Badge>
          <Badge variant={c.prizeStatus === "approved" ? "info" : "gold"}>{c.prizeStatus}</Badge>
        </div>
      </div>

      {row.prizeNote && (
        <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
          <span className="eyebrow" style={{ fontSize: 9.5, marginRight: 6 }}>Request</span>
          {row.prizeNote}
        </p>
      )}
      {prizeText && (
        <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0 }}>
          <span className="eyebrow" style={{ fontSize: 9.5, marginRight: 6 }}>Prize</span>
          {prizeText}
        </p>
      )}

      {finalised && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="eyebrow" style={{ fontSize: 9.5 }}>Winners</span>
          {row.winners.length === 0 ? (
            <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Nobody scored.</span>
          ) : (
            row.winners.map((w) => (
              <div key={w.userId} className="tnum" style={{ fontSize: 12.5, color: "var(--text-secondary)", display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{w.name ?? w.username ?? "Unnamed"}</span>
                <span>{w.value.toLocaleString("en-GB")}</span>
                <Link to="/admin/users" search={{ q: w.email }} style={{ color: "var(--text-accent)" }}>
                  {w.email}
                </Link>
              </div>
            ))
          )}
        </div>
      )}

      {editing ? (
        <PrizeEditor row={row} onDone={() => setEditing(false)} />
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            {c.prizeStatus === "approved" ? "Change prize" : "Attach prize and approve"}
          </Button>
          {c.prizeStatus === "approved" && finalised && row.winners.length > 0 && (
            <Button size="sm" variant="secondary" disabled={grantMutation.isPending} onClick={handleGrant}>
              {grantMutation.isPending ? "Granting…" : "Grant after review"}
            </Button>
          )}
          <Button size="sm" variant="danger" disabled={rejectMutation.isPending} onClick={handleReject}>
            {rejectMutation.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </div>
      )}
      {c.prizeStatus === "approved" && !finalised && (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Grant opens once the results are in.</span>
      )}
      {(grantMutation.isError || rejectMutation.isError) && (
        <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
          {errorMessage(grantMutation.error ?? rejectMutation.error, "That didn't work.")}
        </span>
      )}
    </div>
  );
}

function PrizeQueue() {
  const [status, setStatus] = React.useState<"requested" | "approved">("requested");
  const query = useQuery({
    queryKey: ["admin/challenges", status],
    queryFn: () => fetchAdminChallenges(status),
  });
  const rows = query.data?.items ?? [];

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>Challenge prizes</h2>
        <div style={{ display: "flex", gap: 4 }}>
          {(["requested", "approved"] as const).map((s) => (
            <Button key={s} size="sm" variant={status === s ? "secondary" : "ghost"} onClick={() => setStatus(s)}>
              {s === "requested" ? "Requested" : "Approved"}
            </Button>
          ))}
        </div>
      </div>
      {query.isLoading ? (
        <Skel height="120px" radius="var(--radius-lg)" />
      ) : query.isError ? (
        <span style={{ fontSize: 13, color: "var(--status-danger)" }}>{errorMessage(query.error, "Couldn't load the queue.")}</span>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Trophy size={20} />}
          title={status === "requested" ? "No prize requests" : "No approved prizes waiting"}
          hint={status === "requested" ? "Group admins ask for prizes when they start a challenge." : "Approved prizes show here until they are granted or rejected."}
        />
      ) : (
        rows.map((row) => <PrizeRow key={row.challenge.id} row={row} />)
      )}
    </section>
  );
}

// ─── Groups ───────────────────────────────────────────────────────────────────

function GroupRow({ group }: { group: AdminGroupRow }) {
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => adminDeleteGroup(group.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin/groups"] }),
  });
  const deleted = !!group.deletedAt;

  function handleDelete() {
    if (window.confirm(`Delete "${group.name}"? It disappears for every member. Nobody loses anything they collected.`)) {
      deleteMutation.mutate();
    }
  }

  return (
    <div style={{ ...CARD, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div style={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", overflowWrap: "anywhere" }}>{group.name}</span>
          {deleted && <Badge variant="neutral">Deleted</Badge>}
          {group.openReports > 0 && (
            <Badge variant="danger" icon={<ShieldAlert size={11} />}>
              {group.openReports} report{group.openReports === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        {group.description && (
          <span style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.45, overflowWrap: "anywhere" }}>{group.description}</span>
        )}
        <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {group.memberCount.toLocaleString("en-GB")} members · created {formatDate(group.createdAt)}
          {group.owner && (
            <>
              {" · owner "}
              <Link to="/admin/users" search={{ q: group.owner.email }} style={{ color: "var(--text-accent)" }}>
                {group.owner.username ? `@${group.owner.username}` : group.owner.email}
              </Link>
            </>
          )}
        </span>
        {group.imageUrl && (
          <a href={group.imageUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "var(--text-accent)" }}>
            View image
          </a>
        )}
      </div>
      {!deleted && (
        <Button variant="danger" size="sm" disabled={deleteMutation.isPending} onClick={handleDelete}>
          {deleteMutation.isPending ? "Deleting…" : "Delete group"}
        </Button>
      )}
      {deleteMutation.isError && (
        <span style={{ fontSize: 12, color: "var(--status-danger)", flexBasis: "100%" }}>
          {errorMessage(deleteMutation.error, "Couldn't delete the group.")}
        </span>
      )}
    </div>
  );
}

function GroupList() {
  const [input, setInput] = React.useState("");
  const [q, setQ] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const [reported, setReported] = React.useState(false);
  const [deleted, setDeleted] = React.useState(false);
  const query = useQuery({
    queryKey: ["admin/groups", q, reported, deleted],
    queryFn: () => fetchAdminGroups(q, { reported, deleted }),
  });
  const rows = query.data?.items ?? [];

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>Groups</h2>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, color: "var(--text-secondary)" }}>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={reported} onChange={(e) => setReported(e.target.checked)} /> Reported only
          </label>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="checkbox" checked={deleted} onChange={(e) => setDeleted(e.target.checked)} /> Include deleted
          </label>
        </div>
        <label style={{ position: "relative", display: "flex", alignItems: "center", flex: "0 1 300px", minWidth: 0 }}>
          <Search size={13} style={{ position: "absolute", left: 10, color: "var(--text-muted)" }} aria-hidden="true" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Search name or owner"
            aria-label="Search groups"
            style={{ ...INPUT, width: "100%", paddingLeft: 30 }}
          />
        </label>
      </div>
      {query.isLoading ? (
        <Skel height="90px" radius="var(--radius-lg)" />
      ) : query.isError ? (
        <span style={{ fontSize: 13, color: "var(--status-danger)" }}>{errorMessage(query.error, "Couldn't load groups.")}</span>
      ) : rows.length === 0 ? (
        <EmptyState icon={<UsersRound size={20} />} title={q ? "No groups match" : "No groups yet"} />
      ) : (
        rows.map((g) => <GroupRow key={g.id} group={g} />)
      )}
    </section>
  );
}

export default function AdminGroupsPage() {
  const { user } = useAuth();
  if (!user?.isAdmin) return <AccessDenied />;
  return (
    <AdminShell title="Groups">
      <PrizeQueue />
      <GroupList />
    </AdminShell>
  );
}
